import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, Divider, message, Row, Col } from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  LockOutlined, 
  CheckCircleOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import ReCAPTCHA from 'react-google-recaptcha';

const { Title } = Typography;

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

type SignupResponse = {
  status: 'success' | 'failure';
  message: string;
};

const SignUpPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const signup = async (formData: FormData): Promise<SignupResponse> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/sign-up`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.email,
          password: formData.password,
          first_name: formData.firstName,
          last_name: formData.lastName,
          recaptcha_token: captchaValue, // ← ensure captchaValue is in scope
        }),
      });
  
      // Attempt to parse JSON.
      // Even if the response is not ok, we want to parse the JSON to get any error message.
      const data = await response.json();
  
      if (!response.ok) {
        // e.g., 4xx or 5xx responses
        // The server might return {detail: "..."} on errors or {status: "failure", message: "..."}.
        // Adjust how you handle it if your backend uses a different error shape.
        return {
          status: data.status ?? 'failure',
          message: data.message ?? data.detail ?? 'Sign up failed',
        };
      }
  
      // If response is okay, return data (assuming {status, message})
      return data;
    } catch (error) {
      // Catch lower-level network or JSON parse errors:
      console.error('Sign Up failed:', error);
      return {
        status: 'failure',
        message: 'Unable to connect. Please try again.',
      };
    }
  };

  const handleSubmit = async (values: FormData) => {
    if (!captchaValue) {
      message.error('Please complete the captcha verification');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await signup(values);

      if (response.status === 'success') {
        // Handle success
        message.success(response.message || 'Sign up successful! Check your email to verify your account.');
        navigate('/login');
      } else {
        // Handle failure (e.g., "Email not in allowed domain.")
        message.error(response.message || 'There was an error signing up. Please try again.');
      }
    } catch (error) {
      message.error('Sign up failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  return (
    <div style={{ maxWidth: '500px', margin: '40px auto' }}>
      <Card
        bordered={false}
        style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', borderRadius: '8px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Title level={3}>Create Your Account</Title>
          <Typography.Paragraph type="secondary">
            Please fill in your details to get started
          </Typography.Paragraph>
        </div>
        
        <Form
          form={form}
          name="signup"
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          requiredMark={false}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Please enter your first name' }]}
              >
                <Input 
                  prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} 
                  placeholder="First Name"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Please enter your last name' }]}
              >
                <Input 
                  placeholder="Last Name"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input 
              prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} 
              placeholder="Email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please enter your password' }]}
            hasFeedback
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['password']}
            hasFeedback
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Confirm Password"
            />
          </Form.Item>

          <Form.Item>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LcWGfkqAAAAADnTwBgrSgd7fNZZ0Qhp1yaz8VAo"}
                onChange={handleCaptchaChange}
                theme="light"
              />
            </div>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              icon={<CheckCircleOutlined />}
              size="large"
              block
              disabled={!captchaValue}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </Form.Item>
        </Form>
        
        <Divider>
          <Typography.Text type="secondary">
            Already have an account?
          </Typography.Text>
        </Divider>
        
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          size="large"
          block
          onClick={() => navigate("/login")}
        >
          Back to Login
        </Button>
      </Card>
    </div>
  );
};

export default SignUpPage;